import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import PrivacySafe, { PrivacyBadge } from "@/components/common/PrivacySafe";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line } from "recharts";
import { TrendingUp, Users, Activity, HeartPulse } from "lucide-react";

/** HR-facing privacy-safe analytics.
 *  Never displays personal data. All numbers come from /platform/analytics/*
 *  and /admin/usage/* endpoints which return aggregated counts. */

function toArr(x) {
  return Array.isArray(x)
    ? x
    : x && typeof x === "object"
    ? Object.entries(x).map(([k, v]) => ({ name: k, value: typeof v === "number" ? v : 0 }))
    : [];
}

function ChartCard({ title, subtitle, data, children, testId }) {
  return (
    <div className="rounded-3xl bg-white shadow-card p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">{subtitle}</div>
          <div className="font-heading text-lg text-ink-900">{title}</div>
        </div>
        <PrivacyBadge />
      </div>
      {children}
    </div>
  );
}

export default function Analytics() {
  const t = useT();

  const org = useQuery({
    queryKey: ["analytics-org"],
    queryFn: async () => (await api.get("/platform/analytics/org")).data,
    retry: false,
  });
  const adoption = useQuery({
    queryKey: ["analytics-adoption"],
    queryFn: async () => (await api.get("/admin/usage/adoption")).data,
    retry: false,
  });
  const retention = useQuery({
    queryKey: ["analytics-retention"],
    queryFn: async () => (await api.get("/admin/usage/retention")).data,
    retry: false,
  });
  const wellbeing = useQuery({
    queryKey: ["org-wellbeing-users"],
    queryFn: async () => (await api.get("/org/wellbeing/users")).data,
    retry: false,
  });

  const orgData = org.data || {};
  const activeUsers = Number(orgData.active_users ?? orgData.members ?? orgData.people ?? 0);
  const totalUsers = Number(orgData.total_users ?? orgData.members ?? activeUsers);
  const engagement = Number(orgData.engagement ?? orgData.engagement_pct ?? 0);
  const wellbeingScore = Number(orgData.wellbeing ?? orgData.wellbeing_score ?? 0);

  const adoptionData = toArr(adoption.data?.days || adoption.data?.trend || adoption.data);
  const retentionData = toArr(retention.data?.weeks || retention.data?.cohorts || retention.data);
  const wellbeingList = Array.isArray(wellbeing.data) ? wellbeing.data : wellbeing.data?.items || [];

  const anyLoading = org.isLoading || adoption.isLoading || retention.isLoading || wellbeing.isLoading;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.analytics")} title={t("nav.analytics")} subtitle="Privacy-safe engagement across your organisation." actions={<PrivacyBadge />} />

      {anyLoading && <LoadingCard height="h-24" />}
      {org.isError && <ErrorState message={errMsg(org.error)} onRetry={() => org.refetch()} />}

      <PrivacySafe minUsers={5} count={activeUsers} testId="analytics-privacy-gate">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-3xl bg-white shadow-card p-6" data-testid="analytics-stat-active">
            <div className="flex items-center gap-2 text-ink-500 text-[11px] font-semibold uppercase tracking-[0.15em]">
              <Users className="h-3.5 w-3.5" strokeWidth={1.75} /> Active users
            </div>
            <div className="font-heading text-3xl text-ink-900 mt-2 tracking-tight">{activeUsers || "—"}</div>
            <div className="text-xs text-ink-500 mt-1">of {totalUsers || "—"}</div>
          </div>
          <div className="rounded-3xl bg-white shadow-card p-6" data-testid="analytics-stat-engagement">
            <div className="flex items-center gap-2 text-ink-500 text-[11px] font-semibold uppercase tracking-[0.15em]">
              <Activity className="h-3.5 w-3.5" strokeWidth={1.75} /> Engagement
            </div>
            <div className="font-heading text-3xl text-ink-900 mt-2 tracking-tight">{engagement ? `${Math.round(engagement * (engagement <= 1 ? 100 : 1))}%` : "—"}</div>
          </div>
          <div className="rounded-3xl bg-white shadow-card p-6" data-testid="analytics-stat-wellbeing">
            <div className="flex items-center gap-2 text-ink-500 text-[11px] font-semibold uppercase tracking-[0.15em]">
              <HeartPulse className="h-3.5 w-3.5" strokeWidth={1.75} /> Wellbeing score
            </div>
            <div className="font-heading text-3xl text-ink-900 mt-2 tracking-tight">{wellbeingScore || "—"}</div>
          </div>
          <div className="rounded-3xl bg-white shadow-card p-6" data-testid="analytics-stat-trend">
            <div className="flex items-center gap-2 text-ink-500 text-[11px] font-semibold uppercase tracking-[0.15em]">
              <TrendingUp className="h-3.5 w-3.5" strokeWidth={1.75} /> Trend
            </div>
            <div className="font-heading text-3xl text-ink-900 mt-2 tracking-tight">
              {orgData.trend_direction === "up" ? "↗" : orgData.trend_direction === "down" ? "↘" : "→"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard testId="analytics-adoption" subtitle="Adoption" title="Daily active users">
            {adoptionData.length === 0 ? (
              <div className="text-sm text-ink-500">Not enough activity yet.</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={adoptionData}>
                    <CartesianGrid stroke="#E8E4DD" strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={10} stroke="#8A847C" tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} stroke="#8A847C" tickLine={false} axisLine={false} width={28} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E4DD" }} />
                    <Bar dataKey="value" fill="#728468" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>

          <ChartCard testId="analytics-retention" subtitle="Retention" title="Weekly cohorts">
            {retentionData.length === 0 ? (
              <div className="text-sm text-ink-500">Not enough activity yet.</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={retentionData}>
                    <CartesianGrid stroke="#E8E4DD" strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={10} stroke="#8A847C" tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} stroke="#8A847C" tickLine={false} axisLine={false} width={28} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E4DD" }} />
                    <Line type="monotone" dataKey="value" stroke="#D27D60" strokeWidth={2} dot={{ r: 3, fill: "#D27D60" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>
        </div>

        <div className="rounded-3xl bg-white shadow-card p-6" data-testid="analytics-wellbeing-aggregate">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">Team wellbeing</div>
              <div className="font-heading text-lg text-ink-900">Aggregated by team</div>
              <p className="text-xs text-ink-500 mt-1">
                Only aggregates are visible. Individual chats, journals and reflections are never shared with HR.
              </p>
            </div>
            <PrivacyBadge />
          </div>
          {wellbeingList.length === 0 ? (
            <div className="text-sm text-ink-500">No team data yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {wellbeingList.map((w, i) => (
                <div key={w.id || i} className="rounded-2xl border border-border p-4">
                  <div className="text-ink-900 font-medium">{w.team || w.department || w.name || "Team"}</div>
                  <div className="text-xs text-ink-500 mt-0.5">{w.active_users || w.count || 0} active users</div>
                  {typeof w.score === "number" && (
                    <div className="mt-2 text-sage-700 text-sm">Wellbeing: <span className="font-mono">{w.score}</span></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </PrivacySafe>
    </div>
  );
}
